// Клиентская предоптимизация картинок перед загрузкой на бэк.
// Если картинка большая — ресайзим через canvas до разумного размера и конвертим
// в image/jpeg 0.85 (для webp Safari всё ещё бывает с нюансами по кодированию из canvas).
// Сервер потом дожмёт через sharp → webp.
// SVG, GIF и файлы <= MAX_ORIG_BYTES возвращаем как есть.

const MAX_SIDE = 2400; // px по длинной стороне
const MAX_ORIG_BYTES = 1.5 * 1024 * 1024; // меньше — не трогаем, пусть сжимает бэк
const QUALITY = 0.85;

const SKIP_TYPES = new Set(['image/svg+xml', 'image/gif']);

export async function compressImageForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (SKIP_TYPES.has(file.type)) return file;
  if (file.size <= MAX_ORIG_BYTES) return file;

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = fitInside(bitmap.width, bitmap.height, MAX_SIDE);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY),
    );
    if (!blob || blob.size >= file.size) return file;

    const newName = replaceExt(file.name, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // упадём на <img>
    }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function fitInside(w: number, h: number, max: number) {
  if (w <= max && h <= max) return { width: w, height: h };
  const ratio = w > h ? max / w : max / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

function replaceExt(name: string, ext: string) {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return name + ext;
  return name.slice(0, dot) + ext;
}
